import React, { Children, cloneElement } from 'react'
import PropTypes from 'prop-types'

import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

import tokens from '../../design/tokens'
import colorNames from '../../design/color-names'

const anim = keyframes`
  0% {transform: translateX(-100%); }
  100% {transform: translateX(0); }
`

const StyledTabs = styled.div`
  height: 100%;
  border: 2px solid ${colorNames.geyser};
  background-color: #f7f7f7;
`

const StyledTabList = styled.ul`
  list-style: none;
  height: 41px;
  
`

const StyledTab = styled.li`
  display: inline-block;
  width: 50%;
  text-align: center;

  line-height: 39px;
  cursor: pointer;
  font-weight: 500;
  :first-of-type {
    border-right: 2px solid ${colorNames.geyser};
  }

  transition: 0.25s ease-in-out 0s;
  
  ${({ active }) =>
    active
      ? `  background-color: #f7f7f7;
           border-bottom: 2px solid  #f7f7f7;
          `
      : `  
        background-color: #eee;
        border-bottom: 2px solid ${colorNames.geyser}; 
       
        :hover{
          background-color: #e9e9e9;
        }
        `}

  ${({ disabled }) =>
    disabled ? `
    pointer-events:none; 
    color: ${colorNames.boulder};
    cursor: default;
    font-weight: 400;
    `
      : ''}


`

const StyledTabPanelList = styled.ul`
  list-style: none;

  height: 100%;

  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;

  ${({ dragging }) => (dragging ? 'overflow:initial;' : 'overflow:hidden;')}
`

const StyledTabPanel = styled.li`
  width: 100%;
  flex-shrink: 0;
  position: relative;

  animation: ${anim} 0.2s ease;

  
  ${({ active, index }) =>
    active
      ? 'order: 1; display: initial;'
      : `order: ${2 + index}; display: none;`}
`

const Tab = ({
  className,
  'data-mu-test': dataAttributeTest,
  value,
  label,
  active,
  disabled,
  onChange
}) => {
  const handleClick = () => {
    onChange(value)
  }

  return (
    <StyledTab
      className={className}
      data-mu-test={dataAttributeTest}
      active={active}
      disabled={disabled}
      onClick={handleClick}
    >
      {label}
    </StyledTab>
  )
}

Tab.defaultProp = {
  active: false
}

Tab.propTypes = {
  className: PropTypes.string,
  'data-mu-test': PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.oneOf([PropTypes.string, PropTypes.number]),

  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func
}

export const FormBuilderTabs = ({
  className,
  'data-mu-test': dataAttributeTest,
  value,
  options,
  onChange,
  children,
  dragging
}) => {
  const handleChange = v => {
    typeof onChange === 'function' && v !== value && onChange(v)
  }

  return (
    <StyledTabs className={className} data-mu-test={dataAttributeTest}>
      <StyledTabList>
        {options.map(({ value: itemValue, label, disabled }, i) => (
          <Tab
            key={`tab-${itemValue}-${i}`}
            value={itemValue}
            label={label}
            active={value === itemValue}
            disabled={disabled}
            onChange={handleChange}
          />
        ))}
      </StyledTabList>

      <StyledTabPanelList dragging={dragging}>
        {Children.map(children, (child, index) =>
          cloneElement(child, {
            active: child.props['data-tab'] === value,
            index: index
          })
        )}
      </StyledTabPanelList>
    </StyledTabs>
  )
}

FormBuilderTabs.propTypes = {
  className: PropTypes.string,
  'data-mu-test': PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node,
      disabled: PropTypes.bool
    })
  ),
  onChange: PropTypes.func,
  children: PropTypes.node,
  dragging: PropTypes.bool
}

export const FormBuilderTabPanel = ({
  className,
  'data-mu-test': dataAttributeTest,
  children,
  active,
  index
}) => {
  return (
    <StyledTabPanel
      className={className}
      data-mu-test={dataAttributeTest}
      active={active}
      index={index}
    >
      {children}
    </StyledTabPanel>
  )
}
FormBuilderTabPanel.defaultProp = {
  active: false
}

FormBuilderTabPanel.propTypes = {
  className: PropTypes.string,
  'data-mu-test': PropTypes.string,
  children: PropTypes.node,
  active: PropTypes.bool,
  index: PropTypes.number
}
